import { TAuthSliceState, TSignedInUser } from "./auth/authTypes";
import { TSubscribedMsiVessel, TSubscription, TVesselImageData } from "./subscription/subscriptionTypes";
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ISubscriptionFormData } from "../shared/sharedTypes";
import { ESubscriptionType } from "../shared/sharedEnums";
import { trackEvent, trackExceptionWithContext } from "../services/application-insights/appInsights";
import { AppInsightsActions, AppInsightsComponents, AppInsightsErrorTypes } from "../services/application-insights/appInsightsConstants";


export interface ISubscriptionState {
    userSubscriptions: TSubscription[]; // user ubscriptions
    selectedSubscription: TSubscription | Record<string, never>;
    subscribedMsiVessels: TSubscribedMsiVessel[];
    vesselImages: TVesselImageData[];
    subscribedVesselsOwnerships: string[];
    isLoading: boolean;
    isFetched: boolean;
}


interface IAppState {
    authData: TAuthSliceState;
    subscriptionData: ISubscriptionState;
    error?: string;
}

const initialState: IAppState = {
    authData: {
        userInfo: null,
        isAuthorized: false,
        isLoading: false,
        isFetched: false
    },
    subscriptionData: {
        userSubscriptions: [],
        selectedSubscription: {},
        subscribedMsiVessels: [],
        vesselImages: [],
        subscribedVesselsOwnerships: [],
        isLoading: false,
        isFetched: false
    }
}

const appSlice = createSlice({
    name: 'app',
    initialState,
    reducers: {
        // signOutRequest: (state: IAppState) => {
        //     state.authData.isLoading = false;
        //     state.authData.userInfo = null;
        //     state.authData.isAuthorized = false;
        //     state.authData.isFetched = false;
        //     state.subscriptionData.isFetched = false;
        // },
        // signOutSuccess: () => {
        //     redirectToSignIn();
        // },
        // signOutError: () => {
        //     redirectToSignIn();
        // },

        // Logged in user info and axios/local storage processes
        getUserInfoRequest: (state: IAppState) => {
            // User info fetch is tracked at API layer (authApi.ts)
            state.authData.isLoading = true;
            state.authData.isFetched = false;

            // Subscribed vessels data should be loaded depending on selected subscription - SubscriptionData covers subscriptions, vessels and ownerships
            state.subscriptionData.isFetched = false;
            state.subscriptionData.isLoading = true;
        },
        getUserInfoSuccess: (state: IAppState, { payload: userInfo }: PayloadAction<TSignedInUser>) => {
            const seascapeSubscriptions = userInfo.subscriptions.filter(k=>k.type === ESubscriptionType.Seascape);
            
            // User info success is tracked at API layer (authApi.ts)
            state.authData.userInfo = userInfo;
            state.authData.isAuthorized = true;
            state.authData.isLoading = false;
            state.authData.isFetched = true;
            // This is all subscriptions including seascape and other products, it is filtered in selector
            state.subscriptionData.userSubscriptions = userInfo.subscriptions;
            state.subscriptionData.selectedSubscription = seascapeSubscriptions[0];
        },
        getUserInfoError: (state: IAppState, { payload: error }: PayloadAction<any>) => {
            const errorMessage = (error.response && error.response.data) || error.message || 'Your session has been expired. Please login again.';
            
            trackExceptionWithContext(error instanceof Error ? error : new Error(errorMessage), {
                component: AppInsightsComponents.USER_MANAGEMENT_SLICE,
                action: 'get-user-info-error',
                additionalData: {
                    errorType: AppInsightsErrorTypes.USER_AUTHENTICATION_ERROR,
                    errorMessage,
                    statusCode: error.response?.status,
                    wasAuthorized: state.authData.isAuthorized,
                    hadUserInfo: !!state.authData.userInfo
                }
            });

            state.authData.userInfo = null;
            state.authData.isLoading = false;
            state.authData.isAuthorized = false;
            state.error = errorMessage;
        },

        // Subscription
        getSubscribedMsiVesselsRequest: (state: IAppState, action: PayloadAction<{ selectedSubscriptionData: ISubscriptionFormData }>) => {
            const subscription = state.subscriptionData.userSubscriptions.find((s: TSubscription) => s.id === action.payload.selectedSubscriptionData.id) as TSubscription;
            
            // Subscription data fetch is tracked at API layer (subscriptionApi.ts)
            state.subscriptionData.selectedSubscription = subscription;
            state.subscriptionData.isLoading = true;
            state.subscriptionData.isFetched = false;
        },
        getSubscribedMsiVesselsSuccess: (state: IAppState, action: PayloadAction<{ subscribedMsiVessels: TSubscribedMsiVessel[] }>) => {
            // Subscription data success is tracked at API layer (subscriptionApi.ts)
            state.subscriptionData.isLoading = false;
            state.subscriptionData.subscribedMsiVessels = action.payload.subscribedMsiVessels;
            state.subscriptionData.isFetched = true;
        },
        getSubscribedMsiVesselsError: (state: IAppState, action: PayloadAction<{ message: string }>) => {
            trackExceptionWithContext(new Error(action.payload.message), {
                component: AppInsightsComponents.USER_MANAGEMENT_SLICE,
                action: 'get-subscribed-vessels-error',
                additionalData: {
                    errorType: AppInsightsErrorTypes.SUBSCRIPTION_ERROR,
                    errorMessage: action.payload.message,
                    selectedSubscriptionId: (state.subscriptionData.selectedSubscription as TSubscription)?.id?.toString() || 'unknown',
                    wasLoading: state.subscriptionData.isLoading
                }
            });

            state.subscriptionData.isLoading = false;
            state.subscriptionData.isFetched = false;
            state.error = action.payload.message;
        },
        setSelectedSubscriptionRequest: (state: IAppState, { payload: id }: PayloadAction<number>) => {
            const subscription = state.subscriptionData.userSubscriptions.find((s: TSubscription) => s.id === id) as TSubscription;
            
            trackEvent(AppInsightsActions.SUBSCRIPTION_SELECTION, {
                component: AppInsightsComponents.USER_MANAGEMENT_SLICE,
                action: 'set-selected-subscription',
                subscriptionId: id.toString(),
                subscriptionName: subscription?.name || 'unknown',
                subscriptionType: subscription?.type || 'unknown',
                sectorCount: subscription?.sectors?.length || 0,
                previousSubscriptionId: (state.subscriptionData.selectedSubscription as TSubscription)?.id?.toString() || 'none'
            });

            state.subscriptionData.selectedSubscription = subscription;
        },
        getSubscribedMsiVesselImageDataRequest: (_state: IAppState, _action: PayloadAction<{ sectorCode: string, imoNumber: number }>) => {
            // Vessel image fetch is tracked at saga layer (appSaga.ts)
        },
        getSubscribedMsiVesselImageDataSuccess: (state: IAppState, action: PayloadAction<{ vesselImageData: TVesselImageData }>) => {
            const base64Image = action.payload.vesselImageData.imageData.startsWith("data:image")
                ? action.payload.vesselImageData.imageData
                : `data:image/jpeg;base64,${action.payload.vesselImageData.imageData}`;

            // Vessel image success is tracked at saga layer (appSaga.ts)

            state.subscriptionData.vesselImages.push({ 
                imoNumber: action.payload.vesselImageData.imoNumber, 
                name: action.payload.vesselImageData.name, 
                sourceUrl: action.payload.vesselImageData.sourceUrl, 
                imageData: base64Image 
            });
        },
        getSubscribedMsiVesselImageDataError: (state: IAppState, action: PayloadAction<{ vesselImageData: TVesselImageData }>) => {
            trackExceptionWithContext(new Error('Failed to fetch vessel image data'), {
                component: AppInsightsComponents.USER_MANAGEMENT_SLICE,
                action: 'get-vessel-image-error',
                additionalData: {
                    errorType: AppInsightsErrorTypes.VESSEL_DATA_ERROR,
                    imoNumber: action.payload.vesselImageData.imoNumber.toString(),
                    vesselName: action.payload.vesselImageData.name,
                    errorReason: 'vessel-image-fetch-failed'
                }
            });

            state.subscriptionData.vesselImages.push({ 
                imoNumber: action.payload.vesselImageData.imoNumber, 
                name: action.payload.vesselImageData.name, 
                sourceUrl: action.payload.vesselImageData.sourceUrl, 
                imageData: action.payload.vesselImageData.imageData 
            });
        }
    },
});

export const { getSubscribedMsiVesselsRequest, getSubscribedMsiVesselsSuccess, getSubscribedMsiVesselsError,
    setSelectedSubscriptionRequest, getUserInfoRequest, getUserInfoSuccess,
    getUserInfoError, getSubscribedMsiVesselImageDataRequest, getSubscribedMsiVesselImageDataSuccess, getSubscribedMsiVesselImageDataError } =
    appSlice.actions;
export default appSlice.reducer;